import './index.css';
import RouteErrorPage from './pages/RouteErrorPage';
import ForbiddenPage from './pages/ForbiddenPage';

import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';
import {
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
} from 'react-router-dom';

import { AuthContext } from './AuthContext';

import AuthLayout from './layouts/AuthLayout';
import LoginPage from './pages/AuthPages/LogInPage';

import MainLayout from './layouts/MainLayout';

import TryRNAIPage from './pages/TryRNAIPage';

import HomePage from './pages/ListViews/HomePage';
import PublicationsPage from './pages/ListViews/PublicationsPage';
import AddReferencesFromResources from './pages/ListViews/AddReferencesFromResources';
import PatentsPage from './pages/ListViews/PatentsPage';
import FundingPage from './pages/ListViews/FundingPage';
import PeoplePage from './pages/ListViews/PeoplePage';
import EventsPage from './pages/ListViews/EventsPage';
import JobsPage from './pages/ListViews/JobsPage';
import TutorialsPage from './pages/ListViews/TutorialsPage';
import OrganizationsPage from './pages/ListViews/OrganizationsPage';
import BookmarksPage from './pages/ListViews/BookmarksPage';

import JobForm from './pages/FormPages/JobForm';
import EventForm from './pages/FormPages/EventForm';
import OrganizationForm from './pages/FormPages/OrganizationForm';
import PublicationForm from './pages/FormPages/PublicationForm';
import PatentForm from './pages/FormPages/PatentForm';
import FundingForm from './pages/FormPages/FundingForm';
import AuthorForm from './pages/FormPages/AuthorForm';
import InventorForm from './pages/FormPages/InventorForm';
import PersonForm from './pages/FormPages/PersonForm';
import UserForm from './pages/FormPages/UserForm';

import PublicationPage from './pages/DetailViews/PublicationPage';
import PatentPage from './pages/DetailViews/PatentPage';
import AuthorPage from './pages/DetailViews/AuthorPage';
import InventorPage from './pages/DetailViews/InventorPage';
import GrantPage from './pages/DetailViews/GrantPage';
import EventPage from './pages/DetailViews/EventPage';
import JobPage from './pages/DetailViews/JobPage';
import OrganizationPage from './pages/DetailViews/OrganizationPage';
import TutorialPage from './pages/DetailViews/TutorialPage';
import PersonPage from './pages/DetailViews/PersonPage';

import ProfilePage from './pages/AuthPages/ProfilePage';
import VerticalsPage from './pages/AuthPages/VerticalsPage';
import MyLinks from './pages/AuthPages/MyLinks';

import InviteUsersPage from './pages/AuthPages/InviteUsersPage';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { initializeHotjar } from './hotjar';
import SearchPage from './pages/SearchPage';
import SignUpPage from './pages/AuthPages/SignUpPage';
import SelectRole from './pages/AuthPages/SelectRolePage';
import SelectSubRole from './pages/AuthPages/SelectSubRole';
import DescribeRole from './pages/AuthPages/DescribeRole';
import ForgotPassword from './pages/AuthPages/ForgotPassword';
import SetNewPasswordPage from './pages/AuthPages/SetNewPasswordPage';
import PasswordChangedPage from './pages/AuthPages/PasswordChangedPage';
import SelectPlanPage from './pages/AuthPages/SelectPlanPage';
import VisibilityPage from './pages/VisibilityPage';
import UserPage from './pages/DetailViews/UserPage';
import Subscription from './pages/Subscription/SubscriptionPage';
import FundingFiltersPage from './pages/FundingFiltersPage/FundingFiltersPage';
import SubscriptionExpiredPage from './pages/Subscription/SubscriptionExpiredPage';
import ConfirmDowngradePage from './pages/Subscription/ConfirmDowngradePage';
import ReviewDowngradePage from './pages/Subscription/ReviewDowngradePage';
import SubscriptionSuccessPage from './pages/Subscription/SubscriptionSuccessPage';
import Workbench from './pages/Workbench/Workbench';

import RequireAuth from './RequireAuth';

import ConnectedAccounts from './pages/AuthPages/ConnectedAccounts';
import VideoGuidesPage from './pages/VideoGuidesPage/VideoGuidesPage';

function App() {
  initializeHotjar();

  const [email, setEmail] = useState('');

  const setEmail_ = (email) => {
    setEmail(email);
  };

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route path="/" element={<AuthLayout />} errorElement={<RouteErrorPage />}>
          <Route index element={<LoginPage />} />
          <Route path="login" element={<LoginPage />} />
          <Route path="signup" element={<SignUpPage setEmail={setEmail} />} />
          <Route path="select-role" element={<SelectRole />} />
          <Route path="select-sub-role" element={<SelectSubRole />} />
          <Route path="describe-role" element={<DescribeRole />} />
          <Route path="reset-password" element={<ForgotPassword />} />
          <Route path="set-new-password" element={<SetNewPasswordPage />} />
          <Route path="password-changed" element={<PasswordChangedPage />} />
          <Route path="select-plan" element={<SelectPlanPage />} />
          <Route path="subscription-expired" element={<SubscriptionExpiredPage />} />
          <Route path="confirm-downgrade" element={<ConfirmDowngradePage />} />
          <Route path="review-downgrade" element={<ReviewDowngradePage />} />
          <Route path="subscription-success" element={<SubscriptionSuccessPage />} />
        </Route>

        <Route element={<RequireAuth />}>
          <Route path="forbidden" element={<ForbiddenPage />} />

          <Route element={<MainLayout />} errorElement={<RouteErrorPage />}>
            {/* RNAI Pages */}
            <Route path="workbench/personal" element={<Workbench />} />
            <Route path="tryrnai" element={<TryRNAIPage />} />
            <Route path="visibility" element={<VisibilityPage />} />
            <Route path="video-guides" element={<VideoGuidesPage />} />

            {/* Profile Pages */}
            <Route path="profile/account-details" element={<ProfilePage />} />
            <Route path="profile/draft-verticals" element={<VerticalsPage />} />
            <Route path="profile/my-verticals" element={<VerticalsPage />} />
            <Route path="profile/joined-verticals" element={<VerticalsPage />} />
            <Route path="profile/my-links" element={<MyLinks />} />
            <Route path="profile/subscription" element={<Subscription />} />
            <Route path="profile/funding-filters" element={<FundingFiltersPage />} />
            <Route path="profile/connected-accounts" element={<ConnectedAccounts />} />
          </Route>

          <Route
            path="/vertical/:vertical_id"
            element={<MainLayout />}
            errorElement={<RouteErrorPage />}
          >
            {/* Home Page */}
            <Route path="home" element={<HomePage />} />

            {/* RNAI Page */}
            <Route path="tryrnai" element={<TryRNAIPage />} />

            {/* Search View Page */}
            <Route path="search" element={<SearchPage />} />

            {/* List View Pages */}
            <Route path="people" element={<PeoplePage />} />
            <Route path="funding" element={<FundingPage />} />
            <Route path="publications" element={<PublicationsPage />} />
            <Route
              path="publications/add-references"
              element={<AddReferencesFromResources />}
            />
            <Route path="patents" element={<PatentsPage />} />
            <Route path="organizations" element={<OrganizationsPage />} />
            <Route path="events" element={<EventsPage />} />
            <Route path="tutorials" element={<TutorialsPage />} />
            <Route path="jobs" element={<JobsPage />} />
            <Route path="bookmarks" element={<BookmarksPage />} />
            <Route path="workbench/personal" element={<Workbench />} />
            <Route path="workbench/vertical" element={<Workbench />} />
            <Route path="visibility" element={<VisibilityPage />} />

            {/* Detail View Pages */}
            <Route path="author/:id" element={<AuthorPage />} />
            <Route path="inventor/:id" element={<InventorPage />} />
            <Route path="grants/:id" element={<GrantPage />} />
            <Route path="papers/:id" element={<PublicationPage />} />
            <Route path="patents/:id" element={<PatentPage />} />
            <Route path="organizations/:id" element={<OrganizationPage />} />
            <Route path="events/:id" element={<EventPage />} />
            <Route path="tutorials/:id" element={<TutorialPage />} />
            <Route path="jobs/:id" element={<JobPage />} />
            <Route path="people/:id" element={<PersonPage />} />
            <Route path="users/:id" element={<UserPage />} />

            {/* Edit Pages */}
            <Route path="funding/edit/:id" element={<FundingForm functionality={'edit'} />} />
            <Route path="grants/:id/edit" element={<FundingForm functionality={'edit'} />} />
            <Route path="publications/edit/:id" element={<PublicationForm />} />
            <Route path="papers/:id/edit/" element={<PublicationForm />} />
            <Route path="patents/edit/:id" element={<PatentForm />} />
            <Route path="patents/:id/edit/" element={<PatentForm />} />
            <Route path="people/edit/author/:id" element={<AuthorForm />} />
            <Route path="author/:id/edit" element={<AuthorForm />} />
            <Route path="people/edit/users/:id" element={<UserForm functionality={'edit'} />} />
            <Route path="users/:id/edit" element={<UserForm functionality={'edit'} />} />
            <Route path="people/edit/inventor/:id" element={<InventorForm />} />
            <Route path="inventor/:id/edit" element={<InventorForm />} />
            <Route
              path="organizations/edit/:id"
              element={<OrganizationForm functionality={'edit'} />}
            />
            <Route
              path="organizations/:id/edit"
              element={<OrganizationForm functionality={'edit'} />}
            />
            <Route path="events/edit/:id" element={<EventForm functionality={'edit'} />} />
            <Route path="events/:id/edit" element={<EventForm functionality={'edit'} />} />
            <Route path="jobs/edit/:id" element={<JobForm functionality={'edit'} />} />
            <Route path="jobs/:id/edit" element={<JobForm functionality={'edit'} />} />
            <Route path="people/edit/:id" element={<PersonForm functionality={'edit'} />} />
            <Route path="people/:id/edit" element={<PersonForm functionality={'edit'} />} />

            {/* Add Pages */}
            <Route path="publications/add" element={<PublicationForm functionality={'add'} />} />
            <Route path="funding/add" element={<FundingForm functionality={'add'} />} />
            <Route path="organizations/add" element={<OrganizationForm functionality={'add'} />} />
            <Route path="events/add" element={<EventForm functionality={'add'} />} />
            <Route path="jobs/add" element={<JobForm functionality={'add'} />} />
            <Route path="people/add" element={<PersonForm functionality={'add'} />} />

            {/* Invitation Pages */}
            <Route path="inviteUsers" element={<InviteUsersPage />} />
          </Route>
        </Route>
      </>
    )
  );

  return <RouterProvider router={router} />;
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  // <React.StrictMode>
  <AuthContext>
    <App />
    <ToastContainer />
  </AuthContext>
  // </React.StrictMode>
);
